import React, { useState, useEffect } from "react";
import { Search, User, Phone, MapPin, Menu, X, ChevronDown, LogOut } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isAccediOpen, setIsAccediOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleAccedi = () => {
    setIsAccediOpen(!isAccediOpen);
  };

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          if (currentScrollY > 150 && !isScrolled) {
            setIsScrolled(true);
          } else if (currentScrollY < 50 && isScrolled) {
            setIsScrolled(false);
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isScrolled]);

  const menuItems = [
    { name: "Fisso", path: "/office" },
    { name: "Mobile", path: "/piccole-medie-imprese" },
    { name: "Smartphone", path: "/dispositivi" },
    { name: "M2M", path: "/machine2machine" },
    { name: "Centralini", path: "/office" },
    { name: "ICT", path: "/ict" }
  ];

  return (
    <header className="bg-black text-white fixed top-0 left-0 right-0 z-50 shadow-lg">
      {/* Logo Section */}
      <div 
        className="bg-black border-b border-gray-800 overflow-hidden transition-all duration-500 ease-in-out"
        style={{
          maxHeight: isScrolled ? '0px' : '200px',
          opacity: isScrolled ? 0 : 1
        }}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center space-x-16 py-4">
            {/* Lovecchio Group */}
            <Link to="/" className="flex flex-col items-center hover:opacity-80 transition-opacity duration-200 group">
              <img 
                src="https://customer-assets.emergentagent.com/job_custom-crm/artifacts/tex9m7i1_Copertina%20biografia.jpg"
                alt="Lovecchio Group"
                className="h-20 w-auto mb-3 transform group-hover:scale-105 transition-transform"
              />
              <div className="text-xl font-semibold">
                <span className="text-white">LOVECCHIO</span>
                <span className="text-orange-500 ml-1">GROUP</span>
              </div>
            </Link>
            
            {/* Partner Ufficiale */}
            <Link to="/" className="flex flex-col items-center hover:opacity-80 transition-opacity duration-200 group">
              <img 
                src="https://customer-assets.emergentagent.com/job_site-redesign-7/artifacts/b6fof7pl_logo_w3b_white_footer.png" 
                alt="WindTre Business Partner" 
                className="h-20 w-auto mb-3 transform group-hover:scale-105 transition-transform"
              />
              <div className="text-xl font-semibold">
                <span className="text-white">PARTNER</span>
                <span className="text-orange-500 ml-1">UFFICIALE</span>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Navigation Bar */}
      <nav className="bg-gray-900 border-b border-gray-800">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-3">
            
            {/* Left: Menu Dropdown */}
            <div className="relative">
              <button 
                onClick={toggleMenu}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-all duration-200 border border-gray-700"
              >
                <Menu size={20} />
                <span className="font-semibold">Menu</span>
              </button>
              
              {isMenuOpen && (
                <div className="absolute left-0 top-full mt-2 w-64 bg-gray-800 rounded-2xl shadow-2xl z-50 border border-gray-700 overflow-hidden">
                  <div className="py-2">
                    {menuItems.map((item, idx) => (
                      <Link 
                        key={idx}
                        to={item.path} 
                        className="block px-6 py-4 hover:bg-orange-500 hover:text-white transition-all duration-200 font-medium"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right: Actions */}
            <div className="flex items-center space-x-6">
              <Link to="/trova-agente" className="hover:text-orange-400 transition-colors duration-200 px-3 py-1 text-sm font-medium">Trova Agente</Link>
              <Link to="/supporto" className="hover:text-orange-400 transition-colors duration-200 px-3 py-1 text-sm font-medium">Supporto</Link>
              
              {isAuthenticated ? (
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-300 hidden md:block">{user?.nome}</span>
                  <button onClick={() => navigate(user?.role === 'admin' ? '/dashboard-admin' : '/dashboard-sales')} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors">CRM</button>
                  <button onClick={handleLogout} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors"><LogOut size={16} /></button>
                </div>
              ) : (
                <div className="relative">
                  <button onClick={toggleAccedi} className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg font-semibold transition-all flex items-center space-x-2">
                    <span>Accedi</span>
                    <ChevronDown size={16} className={`transition-transform duration-300 ${isAccediOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isAccediOpen && (
                    <div className="absolute right-0 top-full mt-2 w-56 bg-gray-800 rounded-2xl shadow-2xl z-50 border border-gray-700 overflow-hidden">
                      <div className="py-2">
                        <Link to="/login-crm" className="flex items-center px-4 py-4 hover:bg-gray-700 transition-colors" onClick={() => setIsAccediOpen(false)}>
                          <div className="flex items-center justify-center w-8 h-8 bg-blue-500/20 rounded-full mr-3"><User size={16} className="text-blue-400" /></div>
                          <div><div className="font-semibold text-white">Staff CRM</div></div>
                        </Link>
                        <div className="border-t border-gray-700 mx-4"></div>
                        <Link to="/login-cliente" className="flex items-center px-4 py-4 hover:bg-gray-700 transition-colors" onClick={() => setIsAccediOpen(false)}>
                          <div className="flex items-center justify-center w-8 h-8 bg-green-500/20 rounded-full mr-3"><User size={16} className="text-green-400" /></div>
                          <div><div className="font-semibold text-white">Area Clienti</div></div>
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
